import React from 'react';
import ReactDOM from 'react-dom/client';
import { HashRouter } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { AuthProvider } from './contexts/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';
import { initAnalytics } from './services/analytics';
import './index.css';
import App from './App';

if (process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.NODE_ENV,
    tracesSampleRate: 0.2,
  });
}

// Stale-chunk auto-reload after deploy. When a lazy-loaded chunk file no
// longer exists on the CDN (deploy bumped chunk hashes), Vercel's
// catch-all rewrite serves index.html in place of the missing JS. The
// browser parses '<!doctype html>...' as JS, hits the leading '<', and
// throws SyntaxError. One reload picks up the fresh index.html with
// current chunk URLs.
//
// sessionStorage guard prevents an infinite reload loop on a genuine
// syntax bug — it's reset when the tab closes, so the next session can
// retry once.
function attemptChunkReload() {
  if (sessionStorage.getItem('__chunk_reload_attempted')) return;
  sessionStorage.setItem('__chunk_reload_attempted', '1');
  // Tiny delay so the original error is still visible in DevTools console
  // for anyone who happens to be debugging at that moment.
  setTimeout(() => window.location.reload(), 100);
}

// (1) chunk parsed as HTML — Vercel catch-all served index.html in place of
// a missing JS file. Manifests as SyntaxError 'Unexpected token <'.
window.addEventListener('error', (event) => {
  const isChunkSyntax =
    event.message?.includes('Unexpected token') &&
    event.filename?.includes('.chunk.');
  if (isChunkSyntax) attemptChunkReload();
});

// (2) chunk fetch fails at the network layer (timeout, DNS, transient 5xx)
// or webpack throws ChunkLoadError. These surface as unhandled promise
// rejections from the lazy() dynamic-import.
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;
  const msg = reason?.message || String(reason || '');
  const name = reason?.name || '';
  const isChunkFail =
    name === 'ChunkLoadError' ||
    msg.includes('Loading chunk') ||
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('error loading dynamically imported module') ||
    msg.includes('Importing a module script failed');
  if (isChunkFail) attemptChunkReload();
});

initAnalytics();

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <HashRouter>
        <AuthProvider>
          <App />
          <Analytics />
          <SpeedInsights />
        </AuthProvider>
      </HashRouter>
    </ErrorBoundary>
  </React.StrictMode>
);
